import { useEffect, useState } from 'react'
import { Tag, Check, Pencil, Trash2, Plus, Settings2, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { api, apiError } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import {
  useTags,
  useCreateTag,
  useUpdateTag,
  useDeleteTag,
  useMyPermissions,
  type TagDef,
} from '@/hooks/use-settings'
import { ZaloLabelImportButton } from './zalo-label-import-dialog'

/** Bảng màu thẻ phân loại */
const TAG_COLORS = [
  '#ef4444', // đỏ
  '#ec4899', // hồng
  '#f97316', // cam
  '#f59e0b', // vàng
  '#10b981', // xanh lá
  '#14b8a6', // ngọc
  '#3b82f6', // xanh dương
  '#8b5cf6', // tím
]
const DEFAULT_COLOR = TAG_COLORS[0]
const FALLBACK_COLOR = '#64748b'

/**
 * Quyền quản lý thẻ (BE đòi tags.create / tags.update / tags.delete). Quản lý / Nhân viên mặc định chỉ có
 * tags.view → ẩn hẳn nút (BRD §5.3.1). Gắn/gỡ thẻ có sẵn cho khách vẫn là thao tác hội thoại, không phụ thuộc.
 */
export function useTagPerms() {
  const { data: perms } = useMyPermissions()
  const canCreate = !!perms?.has('tags.create')
  const canUpdate = !!perms?.has('tags.update')
  const canDelete = !!perms?.has('tags.delete')
  return { canCreate, canUpdate, canDelete, canManage: canCreate || canUpdate || canDelete }
}

/** Icon thẻ phân loại tô màu */
function TagIcon({ color, className }: { color: string; className?: string }) {
  return <Tag className={cn('h-4 w-4 shrink-0', className)} style={{ color, fill: color }} />
}

interface Props {
  contactId?: string | null
  tags: string[]
  className?: string
}

export function ConversationTags({ contactId, tags, className }: Props) {
  const { t } = useTranslation('convContact')
  const queryClient = useQueryClient()
  const { data: allTags = [] } = useTags()
  const { canManage } = useTagPerms()
  const [manageOpen, setManageOpen] = useState(false)
  const [savingTag, setSavingTag] = useState<string | null>(null)

  const colorOf = (name: string) =>
    allTags.find((tg) => tg.name === name)?.color ?? FALLBACK_COLOR

  /** Gán/gỡ nhãn — API thay TOÀN BỘ mảng nên luôn gửi lại cả mảng mới */
  async function toggleTag(name: string) {
    if (!contactId || savingTag) return
    const has = tags.includes(name)
    const next = has ? tags.filter((x) => x !== name) : [...tags, name]
    setSavingTag(name)
    try {
      await api.put(`/contacts/${contactId}`, { tags: next })
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
      queryClient.invalidateQueries({ queryKey: ['conversation'] })
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      toast.success(has ? t('convTags.removedToast', { name }) : t('convTags.addedToast', { name }))
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setSavingTag(null)
    }
  }

  return (
    <>
      <div className={cn('flex min-w-0 items-center gap-1', className)}>
        {/* Các nhãn đã gắn */}
        {/* Chỉ 1 nhãn — header còn phải chừa chỗ cho tên khách và các nút bên phải. */}
        {tags.slice(0, 1).map((name) => (
          <span
            key={name}
            className="inline-flex max-w-[9rem] shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium"
            style={{ backgroundColor: `${colorOf(name)}20`, color: colorOf(name) }}
            title={tags.join(', ')}
          >
            <TagIcon color={colorOf(name)} className="h-3 w-3" />
            <span className="truncate">{name}</span>
          </span>
        ))}
        {tags.length > 1 && (
          <span className="shrink-0 text-[11px] text-muted-foreground" title={tags.join(', ')}>
            +{tags.length - 1}
          </span>
        )}

        {/* Nút thẻ: icon khi chưa gắn nhãn nào */}
        {contactId && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={t('convTags.ariaTag')}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <Tag className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-96 w-64 overflow-y-auto">
              {allTags.length === 0 ? (
                <div className="px-2 py-3 text-xs text-muted-foreground">
                  {t('convTags.none')}
                </div>
              ) : (
                allTags.map((tg) => {
                  const checked = tags.includes(tg.name)
                  return (
                    <DropdownMenuItem
                      key={tg.id}
                      onSelect={(e) => {
                        e.preventDefault()
                        toggleTag(tg.name)
                      }}
                      className="gap-2"
                    >
                      <TagIcon color={tg.color} />
                      <span className="flex-1 truncate">{tg.name}</span>
                      {savingTag === tg.name ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                      ) : checked ? (
                        <Check className="h-4 w-4 text-primary" />
                      ) : null}
                    </DropdownMenuItem>
                  )
                })
              )}

              {canManage && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => setManageOpen(true)} className="gap-2">
                    <Settings2 className="h-4 w-4" />
                    {t('convTags.manage')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <ManageTagsDialog open={manageOpen} onOpenChange={setManageOpen} tags={allTags} />
    </>
  )
}

/* ── Popup quản lý thẻ phân loại ─────────────────────────────────── */
/* Export để menu dòng hội thoại (conversation-list) dùng chung. */

export function ManageTagsDialog({
  open,
  onOpenChange,
  tags,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  tags: TagDef[]
}) {
  const { t } = useTranslation('convContact')
  const deleteTag = useDeleteTag()
  const { canCreate, canUpdate, canDelete } = useTagPerms()
  const [editing, setEditing] = useState<TagDef | null>(null)
  const [creating, setCreating] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<TagDef | null>(null)

  async function handleDelete(tag: TagDef) {
    try {
      await deleteTag.mutateAsync(tag.id)
      toast.success(t('convTags.deletedToast', { name: tag.name }))
      setConfirmDelete(null)
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  const formOpen = creating || !!editing

  return (
    <>
      <Dialog open={open && !formOpen} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('convTags.manage')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{t('convTags.listLabel')}</p>

            {tags.length === 0 ? (
              <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                {t('convTags.none')}
              </div>
            ) : (
              <ul className="max-h-80 space-y-1.5 overflow-y-auto">
                {tags.map((tg) => (
                  <li
                    key={tg.id}
                    className="group flex items-center gap-2 rounded-lg bg-muted/60 px-3 py-2"
                  >
                    <TagIcon color={tg.color} />
                    <span className="flex-1 truncate text-sm">{tg.name}</span>
                    {canUpdate && (
                      <button
                        type="button"
                        aria-label={t('convTags.editAria', { name: tg.name })}
                        onClick={() => setEditing(tg)}
                        className="text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        aria-label={t('convTags.deleteAria', { name: tg.name })}
                        onClick={() => setConfirmDelete(tg)}
                        className="text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {canCreate && (
              <Button
                variant="ghost"
                className="w-full justify-start gap-2 text-primary hover:text-primary"
                onClick={() => setCreating(true)}
              >
                <Plus className="h-4 w-4" />
                {t('convTags.addCategory')}
              </Button>
            )}

            {/* Công cụ: kéo nhãn đã gắn ở Zalo PC về (tự ẩn nếu thiếu quyền tạo nhãn / sửa khách). */}
            <ZaloLabelImportButton />
          </div>
        </DialogContent>
      </Dialog>

      {/* Form thêm / sửa */}
      <TagFormDialog
        open={formOpen}
        tag={editing}
        onClose={() => {
          setCreating(false)
          setEditing(null)
        }}
      />

      {/* Xác nhận xóa */}
      <Dialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('convTags.deleteTitle')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            <Trans
              t={t}
              i18nKey="convTags.deleteBody"
              values={{ name: confirmDelete?.name }}
              components={{ b: <span className="font-medium text-foreground" /> }}
            />
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              {t('convTags.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => confirmDelete && handleDelete(confirmDelete)}
              disabled={deleteTag.isPending}
            >
              {t('convTags.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

/* ── Popup thêm mới / sửa thẻ phân loại ──────────────────────────── */

function TagFormDialog({
  open,
  tag,
  onClose,
}: {
  open: boolean
  tag: TagDef | null
  onClose: () => void
}) {
  const { t } = useTranslation('convContact')
  const createTag = useCreateTag()
  const updateTag = useUpdateTag()
  const [name, setName] = useState('')
  const [color, setColor] = useState(DEFAULT_COLOR)

  useEffect(() => {
    if (open) {
      setName(tag?.name ?? '')
      setColor(tag?.color ?? DEFAULT_COLOR)
    }
  }, [open, tag])

  const isEdit = !!tag
  const pending = createTag.isPending || updateTag.isPending

  async function handleSubmit() {
    const trimmed = name.trim()
    if (!trimmed) return
    try {
      if (isEdit) {
        await updateTag.mutateAsync({ id: tag.id, name: trimmed, color })
        toast.success(t('convTags.updatedToast'))
      } else {
        await createTag.mutateAsync({ name: trimmed, color })
        toast.success(t('convTags.addedTagToast'))
      }
      onClose()
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? t('convTags.formEdit') : t('convTags.formNew')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('convTags.nameLabel')}</label>
            <div className="flex items-center gap-2">
              <Input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                placeholder={t('convTags.namePlaceholder')}
                className="flex-1"
              />
              {/* Nút chọn màu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label={t('convTags.changeColor')}
                    className="flex h-9 w-11 shrink-0 items-center justify-center rounded-md border bg-muted transition-colors hover:bg-muted/70"
                  >
                    <TagIcon color={color} className="h-5 w-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-auto p-3">
                  <p className="mb-2 px-1 text-sm font-medium">{t('convTags.changeColor')}</p>
                  <div className="flex gap-1.5">
                    {TAG_COLORS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        aria-label={t('convTags.colorAria', { color: c })}
                        onClick={() => setColor(c)}
                        className="flex h-8 w-8 items-center justify-center rounded-md transition-transform hover:scale-105"
                        style={{ backgroundColor: c }}
                      >
                        {color === c && <Check className="h-4 w-4 text-white" />}
                      </button>
                    ))}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Công cụ: kéo nhãn đã gắn ở Zalo PC về (chỉ khi thêm mới). */}
          {!isEdit && <ZaloLabelImportButton />}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            {t('convTags.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!name.trim() || pending}>
            {isEdit ? t('common:actions.saveChanges') : t('convTags.addCategory')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
